import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useDocumentTitle('Cognidaily | Login');

  // Handle OAuth success redirect
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth');
    if (oauthSuccess === 'success') {
      // Reload to get user data
      window.location.href = '/';
    }
  }, [searchParams]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAppleSignIn = async () => {
    try {
      // Check if Apple Sign In is available
      if (typeof window !== 'undefined' && (window as any).AppleID) {
        const AppleID = (window as any).AppleID;
        AppleID.auth.init({
          clientId: import.meta.env.VITE_APPLE_CLIENT_ID || '',
          scope: 'name email',
          redirectURI: window.location.origin,
          usePopup: true
        });

        const response = await AppleID.auth.signIn();
        
        if (response.authorization) {
          const { id_token, code, user } = response.authorization;
          
          const apiResponse = await fetch('/api/auth/apple', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              identityToken: id_token,
              authorizationCode: code,
              user: user
            })
          });

          const data = await apiResponse.json();
          
          if (data.success) {
            navigate('/');
          } else {
            setError(data.error || 'Apple Sign In failed');
          }
        }
      } else {
        setError('Apple Sign In is not available. Please use a supported browser.');
      }
    } catch (err: any) {
      setError(err.message || 'Apple Sign In failed');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);

    try {
      await login(email, password, rememberMe);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section section--auth">
        <h1>Welcome back</h1>
        <p>Sign in to continue.</p>
        {error && <p role="alert" className="text-error">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label htmlFor="email">Email or Username</label>
            <input type="text" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} />
          </div>
          <div className="form-field">
            <label htmlFor="password">Password</label>
            <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} />
          </div>
          <p><Link to="/forgot-password" className="link">Forgot password?</Link></p>
          <div className="form-field">
            <label><input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} disabled={loading} /> Remember me</label>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Signing in...' : 'Sign in'}</button>
        </form>
        <p>New player? <Link to="/register" className="link">Create an account</Link></p>
    </section>
  );
};

export default Login;